import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AudioToggle } from '@/components/AudioToggle';
import { BrandMark } from '@/components/BrandMark';
import { Icon, type IconName } from '@/components/Icon';
import { Mascot } from '@/components/Mascot';
import { PageTransition } from '@/components/PageTransition';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/lib/store';

const BASE_NAV: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: '/app',          label: '今日', icon: 'today', end: true },
  { to: '/app/courses',  label: '课程', icon: 'courses' },
  { to: '/app/vocab',    label: '单词', icon: 'vocab' },
  { to: '/app/create',   label: '造课', icon: 'create' },
  { to: '/app/mistakes', label: '错题', icon: 'mistakes' },
  { to: '/app/profile',  label: '我的', icon: 'profile' },
];

export function AppShell(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const authUser = useAuth().user;
  const localUser = useAppStore((s) => s.user);
  const progress = useAppStore((s) => s.progress);
  // Prefer the authenticated identity (name/avatar/plan) but fall back to the
  // demo profile if for some reason auth hasn't hydrated yet.
  const user = authUser ?? localUser;
  const NAV: typeof BASE_NAV = authUser?.role === 'admin'
    ? [...BASE_NAV, { to: '/app/admin', label: '后台', icon: 'admin' }]
    : BASE_NAV;

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return (): void => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-ink-950 text-cream-100">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-ink-950/85 backdrop-blur-xl border-b border-white/[0.06]'
            : 'bg-ink-950/40 backdrop-blur'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center gap-4">
          <Link to="/" aria-label="返回官网">
            <BrandMark size="md" />
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 text-sm transition-colors rounded-lg ${
                    isActive
                      ? 'text-honey-300 bg-white/[0.06]'
                      : 'text-cream-300 hover:text-white hover:bg-white/[0.04]'
                  }`
                }
              >
                <Icon name={item.icon} size="md" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 text-xs">
              <StreakBadge days={progress.streakDays} />
              <XpBadge xp={progress.xp} />
            </div>
            <AudioToggle />
            <Link
              to="/app/profile"
              className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-white/[0.06] transition-colors"
              title={user.name}
            >
              <span className="text-xl">{'avatarEmoji' in user ? user.avatarEmoji : '🦊'}</span>
            </Link>
          </div>
        </div>

        {/* Mobile bottom nav strip */}
        <nav className="md:hidden flex items-center overflow-x-auto border-t border-white/[0.06] px-2 no-scrollbar">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-xs transition-colors ${
                  isActive ? 'text-honey-300 border-b-2 border-honey-400' : 'text-cream-400 hover:text-white'
                }`
              }
            >
              <Icon name={item.icon} size="sm" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="h-16" />
      <div className="md:hidden h-10" />

      <main className="max-w-6xl mx-auto px-5 md:px-8 pt-8 pb-20">
        <PageTransition />
      </main>

      <Mascot />
    </div>
  );
}

function StreakBadge({ days }: { days: number }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-ember-500/[0.12] border border-ember-500/30 px-2.5 py-1 text-ember-300">
      <Icon name="streak" size="sm" className="flame-pulse" />
      <span className="font-bold">{days}</span>
      <span className="text-ember-300/70">天</span>
    </span>
  );
}

function XpBadge({ xp }: { xp: number }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-honey-500/[0.12] border border-honey-500/30 px-2.5 py-1 text-honey-300">
      <Icon name="vocab" size="sm" />
      <span className="font-bold">{xp.toLocaleString()}</span>
      <span className="text-honey-300/70">XP</span>
    </span>
  );
}

export { StreakBadge, XpBadge };
